import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import {
  completeAmazonOAuthCallback,
  getLatestChannelOnboardingJob,
  retryChannelOnboardingJob,
} from '../channels/channelsApi'
import type { ChannelConnection, ChannelOnboardingJob, ChannelOnboardingJobStatus, ChannelOnboardingStepStatus } from '../channels/types'
import { clearAmazonOAuthCallback, readAmazonOAuthCallbackParams, type AmazonOAuthCallbackParams } from '../channels/amazonOAuthSession'
import { Button } from '../components/ui/Button'
import { Pill } from '../components/ui/Pill'
import { Icon, I } from '../components/ui/icons'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { gridColumns, mobileButtonStyle } from '../components/ui'

type Phase = 'submitting' | 'success' | 'error'
type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral'

interface AmazonOAuthPageProps {
  accessToken: string
  onDone: () => void
}

const ACTIVE_JOB_STATUSES: ChannelOnboardingJobStatus[] = ['QUEUED', 'RUNNING']

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.amazonOAuth.errors.codes.${error.code}`
  }
  return 'pages.amazonOAuth.errors.codes.REQUEST_FAILED'
}

function isJobActive(job: ChannelOnboardingJob | null): boolean {
  return job !== null && ACTIVE_JOB_STATUSES.includes(job.status)
}

function statusTone(status: ChannelOnboardingJobStatus): PillTone {
  if (status === 'COMPLETED') return 'success'
  if (status === 'COMPLETED_WITH_ISSUES') return 'warning'
  if (status === 'FAILED') return 'danger'
  return 'info'
}

function stepTone(status: ChannelOnboardingStepStatus): PillTone {
  if (status === 'SUCCEEDED') return 'success'
  if (status === 'SUCCEEDED_WITH_ISSUES') return 'warning'
  if (status === 'FAILED') return 'danger'
  if (status === 'RUNNING') return 'info'
  return 'neutral'
}

function isMissingJobError(error: unknown): boolean {
  return error instanceof ApiClientError && error.code === 'CHANNEL_ONBOARDING_JOB_NOT_FOUND'
}

function stripCallbackQuery(): void {
  if (typeof window !== 'undefined') {
    window.history.replaceState({}, '', '/amazon-oauth')
  }
}

function clearStoredCallback(): void {
  clearAmazonOAuthCallback()
  stripCallbackQuery()
}

function shouldClearAfterBackendError(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) return false
  return [
    'CHANNEL_OAUTH_STATE_INVALID',
    'CHANNEL_OAUTH_CALLBACK_FAILED',
    'CHANNEL_EXTERNAL_ACCOUNT_CONFLICT',
    'CHANNEL_CONNECTION_NOT_FOUND',
    'CHANNEL_PROVIDER_UNSUPPORTED',
  ].includes(error.code)
}

function JobStep({ label, status }: { label: string; status: ChannelOnboardingStepStatus }) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '10px 12px', borderRadius: 12, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--fg)' }}>{label}</span>
      <Pill tone={stepTone(status)} size="sm" dot>
        {t(`pages.amazonOAuth.import.stepStatus.${status}`)}
      </Pill>
    </div>
  )
}

function JobProgress({
  job,
  jobError,
  retrying,
  onRetry,
}: {
  job: ChannelOnboardingJob | null
  jobError: unknown
  retrying: boolean
  onRetry: () => void
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()

  if (!job) {
    return (
      <div style={{ marginTop: 20, padding: 16, borderRadius: 14, background: 'var(--brand-100)', border: '1px solid var(--brand-300)', textAlign: 'start' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>{t('pages.amazonOAuth.import.queuedTitle')}</div>
          <Pill tone="info" size="sm" dot>{t('pages.amazonOAuth.import.status.QUEUED')}</Pill>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 6, lineHeight: 1.5 }}>
          {t('pages.amazonOAuth.import.queuedBody')}
        </div>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 20, display: 'grid', gap: 12, textAlign: 'start' }}>
      <div style={{ padding: 16, borderRadius: 14, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>{t('pages.amazonOAuth.import.title')}</div>
            <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 2 }}>{t('pages.amazonOAuth.import.subtitle')}</div>
          </div>
          <Pill tone={statusTone(job.status)} size="sm" dot>
            {t(`pages.amazonOAuth.import.status.${job.status}`)}
          </Pill>
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          <JobStep label={t('pages.amazonOAuth.import.products')} status={job.productStatus} />
          <JobStep label={t('pages.amazonOAuth.import.orders')} status={job.orderStatus} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(2, minmax(0, 1fr))'), gap: 8, marginTop: 12 }}>
          {[
            ['products', job.productUpserted],
            ['orders', job.orderUpserted],
          ].map(([key, value]) => (
            <div key={key} style={{ padding: 10, borderRadius: 10, background: 'white', border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--brand-900)' }}>{value}</div>
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 1 }}>{t(`pages.amazonOAuth.import.counters.${key}`)}</div>
            </div>
          ))}
        </div>

        {job.productIssueCount > 0 && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--warning)', fontWeight: 700 }}>
            {t('pages.amazonOAuth.import.issueCount', { count: job.productIssueCount })}
          </div>
        )}

        {job.errorCode && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger)', fontFamily: 'var(--font-mono)' }}>
            {job.errorCode}
          </div>
        )}

        {job.status === 'FAILED' && (
          <Button kind="coral" size="sm" icon={I.refresh} style={{ marginTop: 14, ...mobileButtonStyle(isMobile) }} onClick={onRetry} disabled={retrying}>
            {retrying ? t('pages.amazonOAuth.import.retrying') : t('pages.amazonOAuth.import.retry')}
          </Button>
        )}
      </div>

      {jobError !== null && !isMissingJobError(jobError) && (
        <div style={{ fontSize: 12.5, color: 'var(--warning)', fontWeight: 700 }}>
          {t('pages.amazonOAuth.import.refreshFailed')}
        </div>
      )}
    </div>
  )
}

export function AmazonOAuthPage({ accessToken, onDone }: AmazonOAuthPageProps) {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<Phase>('submitting')
  const [error, setError] = useState<unknown>(null)
  const [connection, setConnection] = useState<ChannelConnection | null>(null)
  const [job, setJob] = useState<ChannelOnboardingJob | null>(null)
  const [jobError, setJobError] = useState<unknown>(null)
  const [retrying, setRetrying] = useState(false)
  const params = useMemo<AmazonOAuthCallbackParams>(() => readAmazonOAuthCallbackParams(), [])
  const sentRef = useRef(false)
  const isMobile = useIsMobileViewport()

  const fetchLatestJob = useCallback(async (connectionId: number) => {
    try {
      const latest = await getLatestChannelOnboardingJob(accessToken, connectionId)
      setJob(latest)
      setJobError(null)
    } catch (caught) {
      if (isMissingJobError(caught)) {
        setJobError(null)
        return
      }
      setJobError(caught)
    }
  }, [accessToken])

  useEffect(() => {
    if (sentRef.current) return
    sentRef.current = true

    if (params.error) {
      Promise.resolve().then(() => {
        clearStoredCallback()
        setPhase('error')
        setError(new ApiClientError(400, {
          code: 'CHANNEL_OAUTH_CALLBACK_FAILED',
          message: params.errorDescription ?? params.error ?? undefined,
        }))
      })
      return
    }

    if (!params.state) {
      Promise.resolve().then(() => {
        clearStoredCallback()
        setPhase('error')
        setError(new ApiClientError(400, { code: 'CHANNEL_OAUTH_STATE_INVALID' }))
      })
      return
    }

    if (!params.spapiOauthCode && !params.code) {
      Promise.resolve().then(() => {
        clearStoredCallback()
        setPhase('error')
        setError(new ApiClientError(400, { code: 'CHANNEL_OAUTH_CALLBACK_FAILED' }))
      })
      return
    }

    if (!params.sellingPartnerId) {
      Promise.resolve().then(() => {
        clearStoredCallback()
        setPhase('error')
        setError(new ApiClientError(400, { code: 'CHANNEL_OAUTH_CALLBACK_FAILED' }))
      })
      return
    }

    completeAmazonOAuthCallback(accessToken, {
      code: params.code,
      spapiOauthCode: params.spapiOauthCode,
      state: params.state,
      sellingPartnerId: params.sellingPartnerId,
      error: params.error,
      errorDescription: params.errorDescription,
    })
      .then(connected => {
        setConnection(connected)
        setPhase('success')
        clearStoredCallback()
        void fetchLatestJob(connected.id)
      })
      .catch(caught => {
        if (shouldClearAfterBackendError(caught)) {
          clearStoredCallback()
        }
        setPhase('error')
        setError(caught)
      })
  }, [accessToken, fetchLatestJob, params])

  useEffect(() => {
    if (phase !== 'success' || !connection) return
    if (job && !isJobActive(job)) return

    const interval = window.setInterval(() => {
      void fetchLatestJob(connection.id)
    }, 1500)
    return () => window.clearInterval(interval)
  }, [connection, fetchLatestJob, job, phase])

  const handleRetry = async () => {
    if (!job) return
    setRetrying(true)
    try {
      const retried = await retryChannelOnboardingJob(accessToken, job.id)
      setJob(retried)
      setJobError(null)
    } catch (caught) {
      setJobError(caught)
    } finally {
      setRetrying(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '20px 14px' : '32px 28px' }}>
        {phase === 'submitting' && (
          <div style={{ width: 520, maxWidth: '100%', background: 'white', borderRadius: 16, border: '1px solid var(--border-subtle)', padding: isMobile ? 24 : 36, textAlign: 'center', boxShadow: '0 12px 40px rgba(15,23,42,0.06)' }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--brand-100)', color: 'var(--brand-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <Icon size={26}>{I.refresh}</Icon>
            </span>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.015em' }}>
              {t('pages.amazonOAuth.submitting.title')}
            </div>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 8, lineHeight: 1.55 }}>
              {t('pages.amazonOAuth.submitting.body')}
            </p>
          </div>
        )}

        {phase === 'success' && (
          <div style={{ width: 560, maxWidth: '100%', background: 'white', borderRadius: 16, border: '1px solid var(--border-subtle)', padding: isMobile ? 24 : 36, textAlign: 'center', boxShadow: '0 12px 40px rgba(15,23,42,0.06)' }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--success-100)', color: 'var(--success)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <Icon size={26} stroke={3}>{I.check}</Icon>
            </span>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.015em' }}>
              {t('pages.amazonOAuth.success.title')}
            </div>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 8, lineHeight: 1.55 }}>
              {t('pages.amazonOAuth.success.bodyShort')}
            </p>
            <JobProgress job={job} jobError={jobError} retrying={retrying} onRetry={handleRetry} />
            <Button kind="primary" size="md" icon={I.arrowR} style={{ marginTop: 22, ...mobileButtonStyle(isMobile) }} onClick={onDone}>
              {t('pages.amazonOAuth.success.back')}
            </Button>
          </div>
        )}

        {phase === 'error' && (
          <div style={{ width: 480, maxWidth: '100%', background: 'white', borderRadius: 16, border: '1px solid var(--border-subtle)', padding: isMobile ? 24 : 36, textAlign: 'center', boxShadow: '0 12px 40px rgba(15,23,42,0.06)' }}>
            <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--danger-100)', color: 'var(--danger)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <Icon size={26} stroke={3}>{I.alert}</Icon>
            </span>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.015em' }}>
              {t('pages.amazonOAuth.error.title')}
            </div>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 8, lineHeight: 1.55 }}>
              {String(t(errorMessageKey(error), { defaultValue: String(t('pages.amazonOAuth.errors.codes.REQUEST_FAILED')) }))}
            </p>
            <Button kind="secondary" size="md" icon={I.arrowR} style={{ marginTop: 22, ...mobileButtonStyle(isMobile) }} onClick={onDone}>
              {t('pages.amazonOAuth.error.back')}
            </Button>
          </div>
        )}
      </div>

      <div style={{ padding: '16px 28px', borderTop: '1px solid var(--border-subtle)', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12, color: 'var(--fg-subtle)' }}>
          <Icon size={14} color="var(--fg-subtle)">{I.shield}</Icon>
          {t('pages.amazonOAuth.footer.security')}
        </div>
      </div>
    </div>
  )
}
